import { useState, useMemo, useRef } from 'react';

import { X, FileCheck, FileX, Search, SearchCheck, SearchX } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { useDebounce } from 'use-debounce';
import { useOnClickOutside } from 'usehooks-ts';

import { APIBaseChronos } from '@/api/hosts';
import { useGetDocIds } from '@/api/queries/useGetDocIds';
import useGetFetchConfig from '@/api/useGetFetchConfig';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/atoms/Tooltip';
import { cn } from '@/helpers/shadCnUtils';
import { DocIds, MyOptionType } from '@/types';

interface DocumentListProps {
  title: string;
  documents: MyOptionType[];
  onClear: () => void;
  onRemove: (docId: string) => void;
  icon: React.ReactNode;
  variant: 'include' | 'exclude';
}

const DocumentList = ({ title, documents, onClear, onRemove, icon, variant }: DocumentListProps) => {
  const colors = {
    include: {
      bg: 'bg-green-50',
      text: 'text-green-700',
      hover: 'hover:bg-green-100',
    },
    exclude: {
      bg: 'bg-red-50',
      text: 'text-red-700',
      hover: 'hover:bg-red-100',
    },
  };

  const colorScheme = colors[variant];

  return (
    <div
      className={cn(
        'h-24 overflow-y-auto rounded border-gray-200 bg-white transition-all duration-200',
        documents.length === 0 ? 'h-0' : 'my-2 h-24 border',
      )}
    >
      <div className="flex items-center justify-between border-b border-gray-100 bg-gray-50 px-2 py-1.5">
        <div className={cn('flex items-center gap-1 text-xs font-medium', colorScheme.text)}>
          {icon}
          <span>{title}</span>
        </div>
        <button onClick={onClear} className="text-xs text-gray-500 hover:text-gray-700">
          Clear all
        </button>
      </div>
      <div className="flex flex-wrap gap-1.5 p-2">
        {documents.length > 500 ? (
          <div className="flex w-full justify-center p-2 text-xs text-gray-500">
            {variant === 'include' ? `${documents.length} documents...` : `${documents.length} documents...`}
          </div>
        ) : (
          documents.map((doc) => (
            <div
              key={doc?.value}
              className={cn('flex items-center gap-1 rounded px-2 py-1 text-xs', colorScheme.bg, colorScheme.text)}
            >
              <TooltipProvider delayDuration={0}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="max-w-36 truncate">{doc?.label}</span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="text-xs">{doc?.label}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <button
                onClick={() => onRemove(doc?.value || '')}
                className={cn('ml-0.5 rounded p-0.5', colorScheme.text, colorScheme.hover)}
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

interface DocumentFilterProps {
  includedDocs: Map<string, MyOptionType>;
  excludedDocs: Map<string, MyOptionType>;
  setIncludedDocs: (docs: Map<string, MyOptionType>) => void;
  setExcludedDocs: (docs: Map<string, MyOptionType>) => void;
  mode: 'include-facts-from' | 'document-select';
  enabledOnly?: boolean;
}

export const DocumentFilter = ({
  includedDocs,
  excludedDocs,
  setIncludedDocs,
  setExcludedDocs,
  mode,
  enabledOnly = false,
}: DocumentFilterProps) => {
  const [searchParams] = useSearchParams();
  const caseId = searchParams.get('caseId');

  const { fetchConfigGET } = useGetFetchConfig();
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm] = useDebounce(searchTerm, 300);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const dropdownRef = useRef(null);

  // Set a limit of 500 to prevent loading too many docs
  const { data: docIdsResponse } = useGetDocIds(caseId, {
    searchQuery: debouncedSearchTerm,
    limit: 500,
  });

  useOnClickOutside(dropdownRef, () => {
    setSearchTerm('');
    setIsSearchFocused(false);
  });

  const filteredDocuments = useMemo(() => {
    if (!isSearchFocused && !searchTerm) return [];
    return docIdsResponse?.documents ?? [];
  }, [docIdsResponse, searchTerm, isSearchFocused]);

  const totalCount = docIdsResponse?.totalCount ?? 0;

  const handleIncludeAll = async () => {
    // Fetch ALL matching documents without limit
    const url = new URL(`${APIBaseChronos}/api/case/${caseId}/doc-ids`);
    if (debouncedSearchTerm.trim() !== '') {
      url.searchParams.append('search', debouncedSearchTerm.trim());
    }
    const response = await fetch(url.toString(), fetchConfigGET);
    const data = await response.json();
    const allMatchingDocs = data.documents || [];

    // Create a map of the selected docs (removing any documents that are already included)
    const newDocs = new Map<string, MyOptionType>(
      allMatchingDocs
        .filter((doc: { id: string }) => !includedDocs.has(doc.id))
        .map(
          (doc: { id: string; file: { name: string } }) =>
            [doc.id, { label: doc.file.name, value: doc.id }] as [string, MyOptionType],
        ),
    );

    // If any of these documents exist in excluded docs then remove them from the excluded docs
    setExcludedDocs(new Map(Array.from(excludedDocs.entries()).filter(([key]) => !newDocs.has(key))));

    setIncludedDocs(new Map([...includedDocs, ...newDocs]));
    setSearchTerm('');
    setIsSearchFocused(false);
  };

  const handleExcludeAll = async () => {
    // Fetch ALL matching documents without limit
    const url = new URL(`${APIBaseChronos}/api/case/${caseId}/doc-ids`);
    if (debouncedSearchTerm.trim() !== '') {
      url.searchParams.append('search', debouncedSearchTerm.trim());
    }

    const response = await fetch(url.toString(), fetchConfigGET);
    const data = await response.json();
    const allMatchingDocs = data.documents || [];

    // Create a map of the selected docs (removing any documents that are already excluded)
    const newDocs = new Map<string, MyOptionType>(
      allMatchingDocs
        .filter((doc: { id: string }) => !excludedDocs.has(doc.id))
        .map((doc: { id: string; file: { name: string } }) => [doc.id, { label: doc.file.name, value: doc.id }]),
    );

    // If any of these documents exist in included docs then remove them from the included docs
    setIncludedDocs(new Map(Array.from(includedDocs.entries()).filter(([key]) => !newDocs.has(key))));

    setExcludedDocs(new Map([...excludedDocs, ...newDocs]));
    setSearchTerm('');
    setIsSearchFocused(false);
  };

  const handleDocumentSelect = (doc: DocIds, action: 'include' | 'exclude') => {
    const newDoc = { label: doc.file.name, value: doc.id };

    if (action === 'include') {
      if (includedDocs.has(doc.id)) {
        // If already included, remove it
        setIncludedDocs(getUpdatedDocs(doc.id, includedDocs));
      } else {
        // If not included, add it and remove from excluded if present
        setIncludedDocs(new Map([...includedDocs, [doc.id, newDoc]]));
        setExcludedDocs(getUpdatedDocs(doc.id, excludedDocs));
      }
    } else {
      if (excludedDocs.has(doc.id)) {
        // If already excluded, remove it
        setExcludedDocs(getUpdatedDocs(doc.id, excludedDocs));
      } else {
        // If not excluded, add it and remove from included if present
        setExcludedDocs(new Map([...excludedDocs, [doc.id, newDoc]]));
        setIncludedDocs(getUpdatedDocs(doc.id, includedDocs));
      }
    }

    if (filteredDocuments.length === 1) {
      setSearchTerm('');
      setIsSearchFocused(false);
    }
  };

  const getUpdatedDocs = (docId: string, docs: Map<string, MyOptionType>) => {
    return new Map(Array.from(docs.entries()).filter(([key]) => key !== docId));
  };

  return (
    <>
      <div className="flex w-full items-center justify-start gap-2">
        <div className="flex w-full flex-col gap-2">
          <div className="relative w-full">
            <div className="flex gap-2">
              <div className="relative w-full">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onFocus={() => setIsSearchFocused(true)}
                  placeholder="Search documents..."
                  className="w-full rounded border border-gray-200 px-3 py-1.5 text-xs focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none"
                />
                <Search className="absolute top-1/2 right-2 h-3 w-3 -translate-y-1/2 text-gray-400" />
              </div>
            </div>
            {isSearchFocused && filteredDocuments.length > 0 && (
              <div
                ref={dropdownRef}
                className="absolute z-10 mt-1 max-h-42 w-full overflow-y-auto rounded border border-gray-200 bg-white shadow-lg"
              >
                <div className="sticky top-0 border-b bg-gray-50 px-3 py-1.5">
                  <div className="flex items-center justify-between">
                    <div className="text-xs text-gray-500">
                      <span>{totalCount}</span> document{totalCount !== 1 ? 's' : ''} found
                      <span className="text-[10px]">
                        {totalCount > filteredDocuments.length &&
                          ` - (showing ${filteredDocuments.length}, search for more results)`}
                      </span>
                    </div>
                    <div className="flex gap-1">
                      <button
                        onClick={handleIncludeAll}
                        className="flex items-center gap-1 rounded border border-gray-200 px-2 py-0.5 text-xs text-gray-600 transition-colors hover:border-green-200 hover:bg-green-50 hover:text-green-700"
                        title="Include all matching documents in the search results"
                      >
                        <SearchCheck className="h-3 w-3" />
                        {enabledOnly ? 'Select' : 'Include'} {totalCount}
                      </button>

                      {!enabledOnly && (
                        <button
                          onClick={handleExcludeAll}
                          className="flex items-center gap-1 rounded border border-gray-200 px-2 py-0.5 text-xs text-gray-600 transition-colors hover:border-red-200 hover:bg-red-50 hover:text-red-700"
                          title="Exclude all matching documents from the search results"
                        >
                          <SearchX className="h-3 w-3" />
                          Exclude {totalCount}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
                {filteredDocuments.map((doc) => {
                  const isIncluded = includedDocs.has(doc.id);
                  const isExcluded = excludedDocs.has(doc.id);
                  return (
                    <div
                      key={doc.id}
                      className={cn(
                        'group flex cursor-pointer items-center justify-between gap-2 px-3 py-1.5 hover:bg-gray-100',
                        isIncluded
                          ? 'bg-green-50 hover:bg-green-100'
                          : isExcluded
                            ? 'bg-red-50 hover:bg-red-100'
                            : 'bg-white',
                      )}
                      onClick={() => {
                        if (enabledOnly) {
                          handleDocumentSelect(doc, includedDocs.has(doc.id) ? 'exclude' : 'include');
                        }
                      }}
                    >
                      <span className="flex-1 truncate text-xs">{doc.file.name}</span>

                      {!enabledOnly && (
                        <div className="flex gap-1">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDocumentSelect(doc, 'include');
                            }}
                            className={cn(
                              'rounded p-0.5',
                              isIncluded ? 'text-green-500' : 'text-gray-400 hover:text-green-500',
                            )}
                            title="Include document's facts"
                          >
                            <FileCheck className="h-4 w-4" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDocumentSelect(doc, 'exclude');
                            }}
                            className={cn(
                              'rounded p-0.5',
                              isExcluded ? 'text-red-500' : 'text-gray-400 hover:text-red-500',
                            )}
                            title="Exclude document's facts"
                          >
                            <FileX className="h-4 w-4" />
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      <DocumentList
        title={`${mode === 'include-facts-from' ? 'Included facts from' : 'Selected'} ${includedDocs.size} document${includedDocs.size !== 1 ? 's' : ''}`}
        documents={Array.from(includedDocs.values())}
        onClear={() => {
          setIncludedDocs(new Map());
        }}
        onRemove={(docId) => {
          setIncludedDocs(getUpdatedDocs(docId, includedDocs));
        }}
        icon={<FileCheck className="h-3 w-3" />}
        variant="include"
      />

      <DocumentList
        title={`Exclude facts from ${excludedDocs.size} document${excludedDocs.size !== 1 ? 's' : ''}`}
        documents={Array.from(excludedDocs.values())}
        onClear={() => {
          setExcludedDocs(new Map());
        }}
        onRemove={(docId) => {
          setExcludedDocs(getUpdatedDocs(docId, excludedDocs));
        }}
        icon={<FileX className="h-3 w-3" />}
        variant="exclude"
      />
    </>
  );
};
